import React, { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { FileSearch, PackageCheck, Search, Store, Undo2 } from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import { productReturnsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import {
  collapseProductReturnLogsForDisplay,
  exportProductReturnEntry,
  exportProductReturnReport,
} from '../../utils/productReturns';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'Completed', label: 'Completed' },
  { value: 'Incomplete', label: 'Incomplete' },
];
const REASON_OPTIONS = [
  { value: 'all', label: 'All Reasons' },
  { value: 'Damaged', label: 'Damaged' },
  { value: 'Near Expiry', label: 'Near Expiry' },
  { value: 'Expired', label: 'Expired' },
  { value: 'Internal Damage', label: 'Internal Damage' },
];

const todayKey = () => new Date().toISOString().split('T')[0];
const shiftDate = (value, days) => {
  const date = new Date(`${value || todayKey()}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};
const buildRecordSearchText = (record = {}) => [
  record.transferName,
  record.storeName,
  record.sellerName,
  record.status,
].join(' ').toLowerCase();
const buildItemSearchText = (record = {}, item = {}) => [
  buildRecordSearchText(record),
  item.productName,
  item.sku,
  item.reason,
].join(' ').toLowerCase();

const formatTimestamp = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const ProductReturnsReport = () => {
  const { hasFeatureAccess, isAdmin } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const canViewPage = hasFeatureAccess('product_returns.report');
  const selectedStoreContext = useMemo(() => ({
    storeInfoId: selectedStore?.storeInfoId || selectedStore?.store_info_id || null,
    vendorId: selectedStore?.vendorId || selectedStore?.vendor_id || null,
    legacyStoreId: selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || null,
  }), [selectedStore]);
  const hasStoreContext = Boolean(selectedStoreContext.storeInfoId && selectedStoreContext.vendorId);
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [statusFilter, setStatusFilter] = useState('all');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [fromDate, setFromDate] = useState(shiftDate(todayKey(), -30));
  const [toDate, setToDate] = useState(todayKey());
  const [loading, setLoading] = useState(false);
  const loadRecords = useCallback(async () => {
    if (!hasStoreContext) {
      setRecords([]);
      return;
    }
    setLoading(true);
    try {
      const response = await productReturnsAPI.getReport({
        limit: 1000,
        storeInfoId: selectedStoreContext.storeInfoId,
        vendorId: selectedStoreContext.vendorId,
      });
      setRecords(response.data?.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load product return report');
    } finally {
      setLoading(false);
    }
  }, [hasStoreContext, selectedStoreContext.storeInfoId, selectedStoreContext.vendorId]);

  useEffect(() => {
    if (!canViewPage) return undefined;

    loadRecords();
    window.addEventListener('focus', loadRecords);

    return () => {
      window.removeEventListener('focus', loadRecords);
    };
  }, [canViewPage, loadRecords]);
  const handleApproveEdit = async (record) => {
    if (!record?.id) return;
    try {
      await productReturnsAPI.approveEditFromReport({
        id: record.id,
      });
      await loadRecords();
      toast.success('Edit unlock approved');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to approve edit unlock');
    }
  };

  const handleRejectEdit = async (record) => {
    if (!record?.id) return;
    try {
      await productReturnsAPI.rejectEditFromReport({
        id: record.id,
      });
      await loadRecords();
      toast.success('Edit unlock rejected');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reject edit unlock');
    }
  };

  const filteredRecords = useMemo(() => {
    const query = deferredSearch.trim().toLowerCase();
    const fromTimestamp = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : Number.NaN;
    const toTimestamp = toDate ? new Date(`${toDate}T23:59:59`).getTime() : Number.NaN;
    const displayRecords = collapseProductReturnLogsForDisplay(records);

    return displayRecords.flatMap((record) => {
      if (statusFilter !== 'all' && record.status !== statusFilter) return [];
      if (selectedStoreContext.storeInfoId && String(record.storeInfoId || record.storeId || '') !== String(selectedStoreContext.storeInfoId)) {
        return [];
      }

      const recordTimestamp = new Date(record.updatedAt || record.timestamp || record.createdAt || 0).getTime();
      if (Number.isFinite(fromTimestamp) && recordTimestamp < fromTimestamp) return [];
      if (Number.isFinite(toTimestamp) && recordTimestamp > toTimestamp) return [];

      if (!query) return [record];

      const parentMatches = buildRecordSearchText(record).includes(query);
      if (parentMatches) {
        return [record];
      }

      const matchingItems = (record.items || []).filter((item) => buildItemSearchText(record, item).includes(query));
      if (!matchingItems.length) {
        return [];
      }

      return [{
        ...record,
        items: matchingItems,
      }];
    });
  }, [deferredSearch, fromDate, records, selectedStoreContext.storeInfoId, statusFilter, toDate]);

  const tableRows = useMemo(() => {
    const rows = filteredRecords.flatMap((record) => (
      record.items.length
        ? record.items.map((item) => ({
            record,
            item,
          }))
        : [{
            record,
            item: {
              id: `${record.id}-empty`,
              productName: '-',
              sku: '',
              quantity: 0,
              reason: '-',
              manufacturingDate: '',
              expiryDate: '',
            },
          }]
    ));

    return rows.filter(({ item }) => reasonFilter === 'all' || item.reason === reasonFilter);
  }, [filteredRecords, reasonFilter]);

  const summary = useMemo(() => {
    const visibleRecords = Array.from(new Map(tableRows.map(({ record }) => [record.id, record])).values());
    return {
      entries: visibleRecords.length,
      completed: visibleRecords.filter((record) => record.status === 'Completed').length,
      incomplete: visibleRecords.filter((record) => record.status !== 'Completed').length,
      totalQuantity: tableRows.reduce((sum, { item }) => sum + (item.quantity || 0), 0),
    };
  }, [tableRows]);

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Product Returns Report</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 border-b border-slate-200 pb-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[24px] font-bold tracking-tight text-slate-900">Product Returns Report</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Review staff return entries, track completion status, and export seller return records whenever needed.
          </p>
        </div>
        <ExportButton
          onClick={() => exportProductReturnReport(filteredRecords, 'product-returns-report.xlsx')}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.18),_transparent_42%),linear-gradient(135deg,_#f8fafc_0%,_#ffffff_48%,_#f1f5f9_100%)]">
          <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.28) 0.7px, transparent 0.7px)', backgroundSize: '12px 12px' }} />
          <div className="relative flex items-center gap-3">
            <PackageCheck className="h-6 w-6 text-slate-600" />
            <div>
              <p className="text-sm font-medium text-slate-700">Return Entries</p>
              <p className="text-2xl font-bold text-slate-900">{summary.entries}</p>
            </div>
          </div>
        </div>

        <div className="card border border-emerald-200 bg-emerald-50">
          <div className="flex items-center gap-3">
            <PackageCheck className="h-6 w-6 text-emerald-600" />
            <div>
              <p className="text-sm font-medium text-emerald-800">Completed</p>
              <p className="text-2xl font-bold text-emerald-700">{summary.completed}</p>
            </div>
          </div>
        </div>

        <div className="card border border-amber-200 bg-amber-50">
          <div className="flex items-center gap-3">
            <Undo2 className="h-6 w-6 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">Incomplete</p>
              <p className="text-2xl font-bold text-amber-700">{summary.incomplete}</p>
            </div>
          </div>
        </div>

        <div className="card border border-sky-200 bg-sky-50">
          <div className="flex items-center gap-3">
            <Store className="h-6 w-6 text-sky-600" />
            <div>
              <p className="text-sm font-medium text-sky-800">Total Quantity</p>
              <p className="text-2xl font-bold text-sky-700">{summary.totalQuantity}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-end gap-3">
          <label className="relative block min-w-[240px] flex-[1.1_1_300px]">
            <span className="label">Search</span>
            <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
            <input
              className="input pl-10"
              placeholder="Transfer, store, seller, product, SKU..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <FilterSelect
            label="Status"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={setStatusFilter}
            className="w-[170px]"
          />

          <FilterSelect
            label="Reason"
            value={reasonFilter}
            options={REASON_OPTIONS}
            onChange={setReasonFilter}
            className="w-[180px]"
          />

          <div className="w-[140px]">
            <label className="label">Date From</label>
            <input
              className="input text-sm"
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </div>

          <div className="w-[140px]">
            <label className="label">Date To</label>
            <input
              className="input text-sm"
              type="date"
              value={toDate}
              min={fromDate}
              max={todayKey()}
              onChange={(event) => setToDate(event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card table-scroll-shell">
        <table className="min-w-[1460px] divide-y divide-slate-200 text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Transfer Name</th>
              <th className="px-4 py-3 font-semibold">Store</th>
              <th className="px-4 py-3 font-semibold">Seller</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Product</th>
              <th className="px-4 py-3 font-semibold">SKU</th>
              <th className="px-4 py-3 font-semibold text-right">Qty</th>
              <th className="px-4 py-3 font-semibold">Reason</th>
              <th className="px-4 py-3 font-semibold">MFG Date</th>
              <th className="px-4 py-3 font-semibold">Expiry Date</th>
              <th className="px-4 py-3 font-semibold text-right">SKU Count</th>
              <th className="px-4 py-3 font-semibold text-right">Entry Total</th>
              <th className="px-4 py-3 font-semibold">Updated</th>
              <th className="px-4 py-3 font-semibold text-center">Request to Edit</th>
              <th className="px-4 py-3 font-semibold text-center">Export</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {tableRows.map(({ record, item }) => {
              const locked = record.status === 'Completed'
                && (Boolean(record.editControl?.confirmedLocked) || !record.editControl?.unlockedAt);
              const requested = Boolean(record.editControl?.unlockRequested);
              return (
              <tr key={`${record.id}-${item.id}`} className="hover:bg-slate-50/70">
                <td className="px-4 py-3 font-semibold text-slate-900">{record.transferName}</td>
                <td className="px-4 py-3 text-slate-700">{record.storeName || '-'}</td>
                <td className="px-4 py-3 text-slate-700">{record.sellerName || '-'}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                    record.status === 'Completed'
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {record.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-800">{item.productName || '-'}</td>
                <td className="px-4 py-3 font-mono text-slate-500">{item.sku || '-'}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-800">{item.quantity || 0}</td>
                <td className="px-4 py-3 text-slate-700">{item.reason || '-'}</td>
                <td className="px-4 py-3 text-slate-500">{item.manufacturingDate || '-'}</td>
                <td className="px-4 py-3 text-slate-500">{item.expiryDate || '-'}</td>
                <td className="px-4 py-3 text-right text-slate-700">{record.skuCount}</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-800">{record.totalQuantity}</td>
                <td className="px-4 py-3 text-slate-500">{formatTimestamp(record.updatedAt || record.timestamp)}</td>
                <td className="px-4 py-3 text-center">
                  {locked && requested ? (
                    isAdmin() ? (
                      <div className="inline-flex flex-col items-center gap-1">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleApproveEdit(record)}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-indigo-500"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRejectEdit(record)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 transition hover:bg-rose-50"
                          >
                            Reject
                          </button>
                        </div>
                        <span className="text-[11px] text-slate-500">
                          Requested by {record.editControl?.unlockRequestedByName || 'staff'}
                        </span>
                      </div>
                    ) : (
                      <div className="inline-flex flex-col items-center gap-1">
                        <span className="inline-flex rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-700">
                          Requested
                        </span>
                        <span className="text-[11px] text-slate-500">
                          Requested by {record.editControl?.unlockRequestedByName || 'staff'}
                        </span>
                      </div>
                    )
                  ) : record.editControl?.unlockedAt ? (
                    <span className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold text-emerald-700">
                      Approved on {new Date(record.editControl.unlockedAt).toLocaleString()}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <ExportButton
                    size="sm"
                    onClick={() => exportProductReturnEntry(record)}
                  />
                </td>
              </tr>
            );})}
          </tbody>
        </table>

        {!loading && !tableRows.length ? (
          <div className="px-4 py-12 text-center text-sm text-slate-500">
            <FileSearch className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-3">No product returns found for the selected filters.</p>
          </div>
        ) : null}

        {loading ? (
          <div className="px-4 py-12 text-center text-sm text-slate-500">
            Loading product return report...
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProductReturnsReport;
